// components/CourseProgressComponent.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Divider,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import BookIcon from '@mui/icons-material/MenuBook';
import { useTheme } from '@mui/material/styles';
import languageLearningApi from '../middleware/languageLearningApi';

const CourseProgressComponent = ({ userId, courseId, cardStyle }) => {
  const theme = useTheme();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [sections, setSections] = useState([]);
  const [viewMode, setViewMode] = useState('section'); // 'course' or 'section'
  const [activeSection, setActiveSection] = useState(null);
  
  // Fetch data - using the new aggregated endpoint
  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !courseId) {
        console.warn('Missing required data, aborting fetch:', { userId, courseId });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch aggregated course data (single API call for course, sections, and courselets)
        const aggregatedCourseRes = await languageLearningApi.get(`/aggregated/course/${courseId}`);
        const aggregatedCourse = aggregatedCourseRes.data;
        
        // Extract course data
        const course = {
          _id: aggregatedCourse.courseId,
          title: aggregatedCourse.title,
          description: aggregatedCourse.description,
          language: aggregatedCourse.language,
          deckId: aggregatedCourse.deckId,
          createdBy: aggregatedCourse.createdBy,
          createdAt: aggregatedCourse.createdAt
        };
        setCourseData(course);
        
        // Extract sections with courselets
        const transformedSections = aggregatedCourse.sections.map(section => {
          return {
            _id: section.sectionId,
            title: section.title,
            description: section.description,
            order: section.order,
            createdAt: section.createdAt,
            courselets: section.courselets.map(courselet => ({
              _id: courselet.courseletId,
              title: courselet.title,
              domain: courselet.domain,
              order: courselet.order,
              createdAt: courselet.createdAt
            })).sort((a, b) => a.order - b.order)
          };
        }).sort((a, b) => a.order - b.order);
        
        setSections(transformedSections);
        
        // Fetch user progress (still needs a separate call as it's user-specific)
        const progressRes = await languageLearningApi.get(`/learning-queue/user-progress?userId=${userId}&courseId=${courseId}`);
        const progressData = progressRes.data.userCourseProgress;
        setUserProgress(progressData);
        
        // Determine active section
        if (progressData && progressData.activeCourselets && progressData.activeCourselets.length > 0) {
          const activeCourseletId = progressData.activeCourselets[0]; // Get first active courselet
          
          // Find which section contains this active courselet
          let foundActiveSection = null;
          for (const section of transformedSections) {
            const activeCourseletInSection = section.courselets.find(
              courselet => courselet._id === activeCourseletId
            );
            
            if (activeCourseletInSection) {
              foundActiveSection = section;
              break;
            }
          }
          
          // If we couldn't find a section with active courselet, default to first section
          setActiveSection(foundActiveSection || transformedSections[0]);
        } else {
          // If no active courselets, just use the first section
          setActiveSection(transformedSections[0]);
        }
        
      } catch (error) {
        console.error('Error fetching course progress data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, courseId]);
  
  // Toggle view mode
  const toggleViewMode = (e) => {
    if (e) e.stopPropagation(); // Prevent event bubbling
    setViewMode(viewMode === 'course' ? 'section' : 'course');
  };
  
  // Calculate progress statistics
  const calculateProgress = () => {
    if (!sections.length || !userProgress) {
      return {
        totalCourselets: 0,
        completedCourselets: 0,
        progressPercentage: 0,
        sectionsProgress: []
      };
    }
    
    const completedCourseletIds = userProgress.completedCourselets || [];
    const activeCourseletIds = userProgress.activeCourselets || [];
    let totalCourselets = 0;
    let completedCourselets = 0;
    
    const sectionsProgress = sections.map(section => {
      const sectionCourselets = section.courselets || [];
      const sectionTotalCourselets = sectionCourselets.length;
      const sectionCompletedCourselets = sectionCourselets.filter(
        courselet => completedCourseletIds.includes(courselet._id)
      ).length;
      
      totalCourselets += sectionTotalCourselets;
      completedCourselets += sectionCompletedCourselets;
      
      const sectionProgressPercentage = sectionTotalCourselets > 0
        ? Math.round((sectionCompletedCourselets / sectionTotalCourselets) * 100)
        : 0;
        
      return {
        sectionId: section._id,
        sectionTitle: section.title,
        sectionOrder: section.order,
        totalCourselets: sectionTotalCourselets,
        completedCourselets: sectionCompletedCourselets,
        progressPercentage: sectionProgressPercentage,
        courselets: section.courselets.map(courselet => ({
          ...courselet,
          isCompleted: completedCourseletIds.includes(courselet._id),
          isActive: activeCourseletIds.includes(courselet._id)
        }))
      };
    });
    
    const progressPercentage = totalCourselets > 0
      ? Math.round((completedCourselets / totalCourselets) * 100)
      : 0;
      
    return {
      totalCourselets,
      completedCourselets,
      progressPercentage,
      sectionsProgress
    };
  };
  
  const {
    totalCourselets,
    completedCourselets,
    progressPercentage,
    sectionsProgress
  } = calculateProgress();
  
  // Get current view progress data
  const currentViewProgressData = viewMode === 'section' && activeSection 
    ? {
        percentage: sectionsProgress.find(s => s.sectionId === activeSection._id)?.progressPercentage || 0,
        completed: sectionsProgress.find(s => s.sectionId === activeSection._id)?.completedCourselets || 0,
        total: sectionsProgress.find(s => s.sectionId === activeSection._id)?.totalCourselets || 0,
        remaining: (sectionsProgress.find(s => s.sectionId === activeSection._id)?.totalCourselets || 0) -
                  (sectionsProgress.find(s => s.sectionId === activeSection._id)?.completedCourselets || 0)
      }
    : {
        percentage: progressPercentage,
        completed: completedCourselets,
        total: totalCourselets,
        remaining: totalCourselets - completedCourselets
      };
  
  if (loading) {
    return (
      <Card sx={cardStyle}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading course progress...
          </Typography>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card
      sx={{
        ...cardStyle,
        height: '350px',
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ pb: 1, pt: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header Section with Toggle Button moved to the right */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1.5, width: 32, height: 32 }}>
              <BookIcon sx={{ fontSize: "1rem" }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600" lineHeight={1.2}>
                Learning Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeSection ? `Section ${activeSection.order}: ` : ''}
                <span style={{ fontWeight: 'medium' }}>
                  {currentViewProgressData.percentage}%
                </span> complete
              </Typography>
            </Box>
          </Box>
          
          {/* Toggle View Button Moved Here */}
          <Tooltip title={viewMode === 'section' ? "View all sections" : "View current section"}>
            <IconButton 
              size="small" 
              onClick={toggleViewMode}
            >
              {viewMode === 'course' 
                ? <ViewModuleIcon fontSize="small" color="primary" /> 
                : <ViewListIcon fontSize="small" color="primary" />
              }
            </IconButton>
          </Tooltip>
        </Box>

        {/* Progress Statistics */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium" color="success.main" sx={{ mr: 2 }}>
              {currentViewProgressData.completed} Completed
            </Typography>
            <Typography variant="body2" fontWeight="medium" color="primary.main">
              {currentViewProgressData.remaining} Remaining
            </Typography>
          </Box>
          
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={currentViewProgressData.percentage}
              size={48}
              thickness={4}
              sx={{ color: theme.palette.success.main }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                fontWeight="bold"
                color="text.secondary"
              >
                {currentViewProgressData.percentage}%
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Content Area with increased space */}
        <Box sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
          }
        }}>
          {viewMode === 'course' ? (
            // Course View - Show all sections with progress
            <>
              {sectionsProgress.map((section, index) => {
                // Find active section to highlight and show remaining count
                const isActiveSection = activeSection && activeSection._id === section.sectionId;
                const remainingInSection = section.totalCourselets - section.completedCourselets;
                
                return (
                  <Box key={section.sectionId} sx={{ 
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: isActiveSection ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                  }}>
                    <Typography variant="body2" fontWeight={isActiveSection ? "600" : "medium"}>
                      Section {section.sectionOrder}: {section.sectionTitle}
                      {isActiveSection && (
                        <Box component="span" sx={{ color: theme.palette.primary.main, ml: 1 }}>
                          (Current)
                        </Box>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress
                        variant="determinate"
                        value={section.progressPercentage}
                        size={24}
                        thickness={3}
                        sx={{ color: theme.palette.success.main, mr: 1.5 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {section.completedCourselets}/{section.totalCourselets}
                      </Typography>
                    </Box>
                    
                    {isActiveSection && (
                      <Typography variant="caption" 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontWeight: 'medium',
                          display: 'block',
                          mt: 0.5
                        }}
                      >
                        {remainingInSection} courselet{remainingInSection !== 1 ? 's' : ''} remaining in this section
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </>
          ) : (
            // Section View - Show courselets as buttons for the active section only
            <>
              {activeSection && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: theme.palette.primary.main }}>
                    Current: Section {activeSection.order} - {activeSection.title}
                  </Typography>
                  
                  <Grid container spacing={1.5}>
                    {sectionsProgress
                      .find(section => section.sectionId === activeSection._id)?.courselets
                      .map((courselet) => (
                        <Grid item key={courselet._id}>
                          <Tooltip title={courselet.title} arrow>
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                bgcolor: courselet.isCompleted 
                                  ? theme.palette.success.main 
                                  : courselet.isActive
                                    ? theme.palette.primary.main
                                    : theme.palette.grey[200],
                                color: courselet.isCompleted || courselet.isActive 
                                  ? 'white' 
                                  : theme.palette.text.secondary,
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                position: 'relative'
                              }}
                            >
                              {courselet.isCompleted ? (
                                <CheckCircleIcon sx={{ fontSize: '1.2rem' }} />
                              ) : courselet.isActive ? (
                                <PlayIcon sx={{ fontSize: '1.2rem' }} />
                              ) : (
                                `${activeSection.order}.${courselet.order || '?'}`
                              )}
                            </Box>
                          </Tooltip>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CourseProgressComponent;